import { log } from '../services/production-logger';

/**
 * Auth Logger Utility
 * 
 * Production-ready logging utility for authentication events.
 * Provides structured logging with appropriate log levels and
 * ensures sensitive data is never logged in production.
 */

// Global __DEV__ declaration for TypeScript
declare const __DEV__: boolean;

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  userId?: string;
  email?: string;
  action?: string;
  error?: any;
  metadata?: Record<string, any>;
}

class AuthLogger {
  private isDevelopment = __DEV__;
  private logPrefix = '[Auth]';

  /**
   * Log debug information (development only)
   */
  debug(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      log.debug(`${this.logPrefix} DEBUG:`, message, this.sanitizeContext(context));
    }
  }

  /**
   * Log general information
   */
  info(message: string, context?: LogContext): void {
    const sanitizedContext = this.sanitizeContext(context);
    
    if (this.isDevelopment) {
      log.debug(`${this.logPrefix} INFO:`, message, sanitizedContext);
    } else {
      // In production, send to monitoring service
      this.sendToMonitoring('info', message, sanitizedContext);
    }
  }

  /**
   * Log warnings
   */
  warn(message: string, context?: LogContext): void {
    const sanitizedContext = this.sanitizeContext(context);
    
    if (this.isDevelopment) {
      log.warn(`${this.logPrefix} WARN:`, message, sanitizedContext);
    } else {
      // In production, send to monitoring service
      this.sendToMonitoring('warn', message, sanitizedContext);
    }
  }

  /**
   * Log errors
   */
  error(message: string, error: any, context?: LogContext): void {
    const sanitizedContext = this.sanitizeContext(context);
    const errorInfo = this.extractErrorInfo(error);
    
    if (this.isDevelopment) {
      log.error(`${this.logPrefix} ERROR:`, message, errorInfo, sanitizedContext);
    } else {
      // In production, send to monitoring service
      this.sendToMonitoring('error', message, {
        ...sanitizedContext,
        error: errorInfo,
      });
    }
  }

  /**
   * Log authentication events for analytics
   */
  logAuthEvent(event: string, context?: LogContext): void {
    const sanitizedContext = this.sanitizeContext(context);
    
    // Log to analytics service
    try {
      // Import dynamically to avoid circular dependencies
      import('../services/analytics-service').then(({ logEvent }) => {
        logEvent(`auth_${event}`, sanitizedContext);
      });
    } catch (error) {
      this.warn('Failed to log auth event to analytics', { error });
    }
  }

  /**
   * Sanitize context to remove sensitive data in production
   */
  private sanitizeContext(context?: LogContext): Record<string, any> {
    if (!context) return {};

    const sanitized: Record<string, any> = {};

    // Always include userId if available
    if (context.userId) {
      sanitized.userId = context.userId;
    }

    // In development, include email; in production, only domain
    if (context.email) {
      if (this.isDevelopment) {
        sanitized.email = context.email;
      } else {
        const [, domain] = context.email.split('@');
        sanitized.emailDomain = domain;
      }
    }

    // Include action if provided
    if (context.action) {
      sanitized.action = context.action;
    }

    // Include metadata but filter sensitive fields
    if (context.metadata) {
      sanitized.metadata = this.filterSensitiveData(context.metadata);
    }

    return sanitized;
  }

  /**
   * Extract useful information from errors
   */
  private extractErrorInfo(error: any): Record<string, any> {
    if (!error) return { message: 'Unknown error' };

    const errorInfo: Record<string, any> = {
      message: error.message || 'Unknown error',
      code: error.code,
      name: error.name,
    };

    // Include stack trace in development only
    if (this.isDevelopment && error.stack) {
      errorInfo.stack = error.stack;
    }

    // Firebase auth specific error info
    if (error.code?.startsWith('auth/')) {
      errorInfo.authError = true;
      errorInfo.errorType = error.code.replace('auth/', '');
    }

    return errorInfo;
  }

  /**
   * Filter out sensitive data from objects
   */
  private filterSensitiveData(data: Record<string, any>): Record<string, any> {
    const sensitiveKeys = ['password', 'token', 'secret', 'key', 'credit'];
    const filtered: Record<string, any> = {};

    Object.keys(data).forEach(key => {
      const lowerKey = key.toLowerCase();
      const isSensitive = sensitiveKeys.some(sensitive => lowerKey.includes(sensitive));
      
      if (!isSensitive) {
        filtered[key] = data[key];
      } else if (this.isDevelopment) {
        // In development, show that the field exists but redact the value
        filtered[key] = '[REDACTED]';
      }
    });

    return filtered;
  }

  /**
   * Send logs to monitoring service in production
   */
  private sendToMonitoring(level: LogLevel, message: string, context: Record<string, any>): void {
    // In a real production app, this would send to services like:
    // - Sentry
    // - LogRocket
    // - CloudWatch
    // - Custom logging service
    
    // For now, we'll store in a buffer that can be sent in batches
    try {
      const logEntry = {
        timestamp: new Date().toISOString(),
        level,
        message,
        context,
        platform: 'mobile',
        version: '1.0.0', // Should come from app config
      };

      // TODO: Implement actual monitoring service integration
      // Example: Sentry.captureMessage(message, level);
      
      // For now, just ensure we don't lose critical errors
      if (level === 'error') {
        log.error('[Production Log]', logEntry);
      }
    } catch (error) {
      // Don't let logging errors break the app
      log.error('Failed to send log to monitoring:', error);
    }
  }
}

// Export singleton instance
export const authLogger = new AuthLogger();

// Export convenience methods
export const logAuthDebug = (message: string, context?: LogContext) => 
  authLogger.debug(message, context);

export const logAuthInfo = (message: string, context?: LogContext) => 
  authLogger.info(message, context);

export const logAuthWarn = (message: string, context?: LogContext) => 
  authLogger.warn(message, context);

export const logAuthError = (message: string, error: any, context?: LogContext) => 
  authLogger.error(message, error, context);

export const logAuthEvent = (event: string, context?: LogContext) => 
  authLogger.logAuthEvent(event, context);